/**
 * The canonical artifact viewer.
 *
 * One component — chrome + body + dialogs — for every context that needs to
 * show an artifact: the standalone /artifacts/:id page, the surface slot
 * beside the chat rail, and the workflow editor's embedded panel. Callers
 * supply only the chrome that differs (the page adds a sidebar trigger and
 * back button; the surface and panel supply a close callback).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  Workflow,
  Share2,
  History,
  Loader2,
  AlertCircle,
  X,
} from 'lucide-react';

import { ShareModal } from '@/components/share-modal';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  getFileIcon,
  getFileIconBackgroundColor,
} from '@/constants/node-styling';
import { useArtifactVersions } from '@/hooks/queries/artifacts/use-artifact-versions';
import type { ArtifactVersionEntry } from '@/hooks/queries/artifacts/use-artifact-versions';
import { useGetArtifact } from '@/hooks/queries/artifacts/use-get-artifact';
import { cn } from '@/lib/utils';
import {
  ArtifactOverflowMenu,
  ArtifactRenameDialog,
  ArtifactDeleteDialog,
} from '@/components/artifacts/artifact-action-dialogs';
import {
  useArtifactActions,
  artifactFileToNodeFile,
  refreshNodeFile,
} from '@/components/artifacts/use-artifact-actions';
import { useArtifactRole } from '@/components/artifacts/use-artifact-role';
import { useOrgStore } from '@/stores/org-store';
import type { ArtifactListItem } from '@/types/artifacts';
import type { NodeFile } from '@/types/node-types';
import type { ArtifactShareRole } from '@/types/share-types';

import { FileRenderer } from './artifact-file-renderer';

/** Keeps the role/action hooks off a null artifact while the detail loads. */
const EMPTY_ARTIFACT: ArtifactListItem = {
  id: '',
  title: '',
  type: 'other',
  status: 'active',
  thumbnail_status: 'pending',
  client_data: false,
  owner_id: '',
  created_at: '',
  updated_at: '',
};

function versionLabel(entry: ArtifactVersionEntry) {
  if (entry.version !== null) return `v${entry.version}`;
  return new Date(entry.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function VersionChips({
  versions,
  shownId,
  onSelect,
}: {
  versions: ArtifactVersionEntry[];
  shownId: string;
  onSelect: (artifactId: string) => void;
}) {
  return (
    <div
      role='group'
      aria-label='Artifact versions'
      className='flex shrink-0 items-center justify-end gap-1 overflow-x-auto border-b border-border bg-background px-2 py-1'
    >
      <History className='size-3 shrink-0 text-muted-foreground' />
      {versions.map((entry) => {
        const isCurrent = entry.artifactId === shownId;
        return (
          <Button
            key={entry.artifactId}
            variant='outline'
            size='sm'
            disabled={isCurrent}
            title={entry.editSummary ?? entry.title}
            onClick={() => onSelect(entry.artifactId)}
            className={cn(
              'h-6 shrink-0 rounded-full px-2 text-[11px] shadow-none',
              isCurrent
                ? 'border-primary/40 bg-primary/10 text-primary disabled:opacity-100'
                : 'border-border/60 text-muted-foreground',
            )}
          >
            {versionLabel(entry)}
          </Button>
        );
      })}
    </div>
  );
}

export interface ArtifactViewerProps {
  artifactId: string;
  /**
   * user_role from the list item that opened this viewer. The detail endpoint
   * does not return it, so the opener has to carry it across.
   */
  userRole?: ArtifactShareRole;
  /** Root height class: the page fills the viewport, the panel its container. */
  rootClassName?: string;
  titleMaxWidthClass?: string;
  /** Chrome rendered before the title pill (sidebar trigger, back button). */
  leading?: ReactNode;
  onClose: () => void;
  onViewWorkflow?: (workflowId: string, runId?: string) => void;
  /** Where to go once the artifact is deleted and this view is meaningless. */
  onAfterDelete: () => void;
  /** Offered when the artifact cannot be loaded at all. */
  loadErrorAction?: ReactNode;
}

export function ArtifactViewer({
  artifactId,
  userRole,
  rootClassName = 'h-full',
  titleMaxWidthClass = 'max-w-[320px]',
  leading,
  onClose,
  onViewWorkflow,
  onAfterDelete,
  loadErrorAction,
}: ArtifactViewerProps) {
  const { currentOrg } = useOrgStore();
  const {
    data: artifact,
    isLoading,
    error,
  } = useGetArtifact(artifactId ?? null);

  // Keyed on the artifact so pointing the viewer elsewhere drops the
  // selection in the same render, not an effect later.
  const [versionSelection, setVersionSelection] = useState<{
    artifactId: string;
    versionId: string;
  } | null>(null);
  const versionId =
    versionSelection?.artifactId === artifactId
      ? versionSelection.versionId
      : null;
  const selectVersion = useCallback(
    (id: string) =>
      setVersionSelection(
        id === artifactId ? null : { artifactId, versionId: id },
      ),
    [artifactId],
  );
  const { data: versionArtifact, error: versionError } =
    useGetArtifact(versionId);
  const { data: versions } = useArtifactVersions(artifact ?? null);
  const shown = versionArtifact ?? artifact;
  const isPastVersion = versionId !== null;

  const [refreshedFile, setRefreshedFile] = useState<NodeFile | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  const primaryFile = useMemo(
    () => shown?.files?.find((f) => f.role === 'primary') ?? null,
    [shown],
  );

  useEffect(() => {
    setRefreshedFile(null);
    if (!primaryFile || !currentOrg?.orgid) return;
    let cancelled = false;
    const nodeFile = artifactFileToNodeFile(primaryFile);
    refreshNodeFile(currentOrg.orgid, nodeFile).then((refreshed) => {
      if (!cancelled) setRefreshedFile(refreshed);
    });
    return () => {
      cancelled = true;
    };
  }, [primaryFile, currentOrg?.orgid]);

  // Rename/share/delete manage the document, i.e. the tip: deleting a past
  // revision would close the panel on a live tip and cut the lineage chain,
  // so those stay hidden while a past revision is shown. Download follows
  // the shown file.
  const artifactAsListItem: ArtifactListItem = artifact
    ? {
        id: artifact.id,
        title: artifact.title,
        type: artifact.type,
        status: artifact.status,
        thumbnail_status: artifact.thumbnail_status,
        client_data: artifact.client_data,
        owner_id: artifact.owner_id,
        created_at: artifact.created_at,
        updated_at: artifact.updated_at,
        user_role: artifact.user_role ?? userRole,
      }
    : { ...EMPTY_ARTIFACT, id: artifactId, user_role: userRole };

  const role = useArtifactRole(artifactAsListItem);
  const canRename = role.canRename && !isPastVersion;
  const canShare = role.canShare && !isPastVersion;
  const canDelete = role.canDelete && !isPastVersion;
  const actions = useArtifactActions(artifactAsListItem);

  const handleDownload = useCallback(() => {
    const fileToDownload = primaryFile
      ? artifactFileToNodeFile(primaryFile)
      : null;
    void actions.handleDownload(fileToDownload);
  }, [actions, primaryFile]);

  const handleViewWorkflow = useCallback(() => {
    if (!shown?.source_workflow_id) return;
    onViewWorkflow?.(
      shown.source_workflow_id,
      shown.source_run_id ?? undefined,
    );
  }, [shown, onViewWorkflow]);

  const FileIcon = shown ? getFileIcon(shown.title) : null;
  const accent = shown ? getFileIconBackgroundColor(shown.title) : '';

  if (isLoading) {
    return (
      <div
        className={`flex ${rootClassName} items-center justify-center bg-background`}
      >
        <Loader2 className='size-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  // Only a hard failure when there is no cached data to fall back on: a
  // background refetch error (focus refresh, stale-time refresh) shouldn't
  // blow away an artifact that is already on screen.
  if (!shown) {
    if (error) {
      return (
        <div
          className={`flex ${rootClassName} flex-col items-center justify-center gap-2 bg-background text-muted-foreground`}
        >
          <AlertCircle className='size-8' />
          <p className='text-sm'>Failed to load artifact.</p>
          {loadErrorAction}
        </div>
      );
    }
    return null;
  }

  let body: ReactNode;
  if (isPastVersion && !versionArtifact) {
    body = versionError ? (
      <div className='flex h-full flex-col items-center justify-center gap-2 text-muted-foreground'>
        <AlertCircle className='size-8' />
        <p className='text-sm'>Failed to load this version.</p>
      </div>
    ) : (
      <div className='flex h-full items-center justify-center'>
        <Loader2 className='size-6 animate-spin text-muted-foreground' />
      </div>
    );
  } else if (primaryFile) {
    body = (
      <FileRenderer
        file={primaryFile}
        refreshedFile={refreshedFile}
        artifactType={shown.type}
        onDownload={handleDownload}
        isDownloading={actions.isDownloading}
      />
    );
  } else {
    body = (
      <div className='flex h-full flex-col items-center justify-center gap-2 text-muted-foreground'>
        <AlertCircle className='size-8' />
        <p className='text-sm'>No preview available for this artifact.</p>
      </div>
    );
  }

  return (
    <div
      className={`flex ${rootClassName} flex-col overflow-hidden bg-background`}
    >
      <div className='flex shrink-0 items-center justify-between border-b border-border bg-background px-2 py-2'>
        <div className='flex items-center gap-1'>
          {leading}
          {/* Title and close read as one segmented control: only the group's
              outer corners are rounded, so the two sit flush. */}
          <div
            className={cn(
              'flex items-center gap-0.5',
              '*:rounded-none',
              '[&>*:first-child]:rounded-l-md [&>*:last-child]:rounded-r-md',
            )}
          >
            {/* A label, not a control — styled off the button variants so it
                matches the close button it sits against. The svg override
                keeps the file glyph inside its accent tile, since the button
                base would otherwise size it to the tile itself. */}
            <div
              className={cn(
                buttonVariants({ variant: 'outline' }),
                titleMaxWidthClass,
                'cursor-default [&_svg]:size-3',
              )}
            >
              {FileIcon && (
                <div
                  className='flex size-5 shrink-0 items-center justify-center rounded'
                  style={{ backgroundColor: accent }}
                >
                  <FileIcon className='size-3 text-white dark:text-foreground' />
                </div>
              )}
              <span className='truncate'>{shown.title}</span>
            </div>

            <Button
              variant='outline'
              size='icon'
              aria-label='Close artifact viewer'
              onClick={onClose}
            >
              <X className='size-4' />
            </Button>
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <div onClick={(e) => e.stopPropagation()}>
            <ArtifactOverflowMenu
              onDownload={handleDownload}
              isDownloading={actions.isDownloading}
              canRename={canRename}
              onRename={actions.openRenameDialog}
              canShare={canShare}
              onShare={() => setShareOpen(true)}
              canDelete={canDelete}
              onDelete={() => actions.setDeleteDialogOpen(true)}
              triggerClassName='h-9 w-9 shrink-0'
            />
          </div>

          {canShare && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='outline'
                  size='icon'
                  className='h-9 w-9 shrink-0'
                  onClick={() => setShareOpen(true)}
                >
                  <Share2 className='size-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='bottom'>Share</TooltipContent>
            </Tooltip>
          )}

          {shown.source_workflow_id && onViewWorkflow && (
            <Button
              variant='outline'
              className='h-9 gap-2 text-sm font-medium'
              onClick={handleViewWorkflow}
            >
              <Workflow className='size-4' />
              View Workflow
            </Button>
          )}
        </div>
      </div>

      {versions && versions.length > 1 && (
        <VersionChips
          versions={versions}
          shownId={versionId ?? artifactId}
          onSelect={selectVersion}
        />
      )}

      <div className='relative min-h-0 flex-1 overflow-hidden bg-background'>
        {body}
      </div>

      <ArtifactRenameDialog
        open={actions.renameDialogOpen}
        onOpenChange={actions.setRenameDialogOpen}
        value={actions.renameValue}
        onChange={actions.setRenameValue}
        onConfirm={actions.handleRename}
        isPending={actions.renamePending}
      />

      <ArtifactDeleteDialog
        open={actions.deleteDialogOpen}
        onOpenChange={actions.setDeleteDialogOpen}
        title={artifactAsListItem.title}
        onConfirm={() => actions.handleDelete(onAfterDelete)}
        isPending={actions.deletePending}
      />

      {shareOpen && (
        <ShareModal
          resourceType='artifact'
          resourceId={artifactAsListItem.id}
          orgId={currentOrg?.orgid ?? null}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}
    </div>
  );
}
